import React, { useState, useRef } from 'react';

const ClusterSelection = ({
  llmRecommendations,
  summary,
  onSubmit,
  loading
}) => {
  const [selectedClusters, setSelectedClusters] = useState([]);
  const [customCareerChoice, setCustomCareerChoice] = useState('');
  const [error, setError] = useState('');
  const errorRef = useRef(null);

  const toggleCluster = (clusterName) => {
    setError('');
    setSelectedClusters(prev => {
      if (prev.includes(clusterName)) {
        return prev.filter(c => c !== clusterName);
      } else {
        return [...prev, clusterName];
      }
    });
  };

  const handleSubmit = () => {
    if (selectedClusters.length === 0) {
      setError('Please select at least one career cluster from the list above before continuing.');
      setTimeout(() => errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
      return;
    }

    onSubmit(selectedClusters, customCareerChoice.trim() || null);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <svg
              className="w-8 h-8 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Career Recommendations
          </h1>
          <p className="text-gray-600">
            Based on your interests, we've identified career clusters that match your profile
          </p>
        </div>

        {/* AI Summary */}
        {summary && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-gray-800 mb-2">Analysis Summary</h3>
            <p className="text-gray-700">{summary}</p>
          </div>
        )}

        {/* Instructions */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <svg
              className="w-5 h-5 text-yellow-600 mt-0.5 mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
            <div>
              <p className="font-medium text-gray-800">Select Your Career Interests</p>
              <p className="text-sm text-gray-600 mt-1">
                Review the recommendations below and select at least one career cluster that interests you.
                You can select multiple clusters.
              </p>
            </div>
          </div>
        </div>

        {/* Recommended Clusters */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-4">
            Recommended Career Clusters ({llmRecommendations.length})
          </h2>
          <div className="space-y-3">
            {llmRecommendations.map((cluster) => {
              const isSelected = selectedClusters.includes(cluster.clusterName);
              return (
                <div
                  key={cluster.clusterName}
                  onClick={() => toggleCluster(cluster.clusterName)}
                  className={`
                    border-2 rounded-lg p-4 cursor-pointer transition-all duration-200
                    ${isSelected
                      ? 'border-blue-500 bg-blue-50 shadow-md'
                      : 'border-gray-300 bg-white hover:border-blue-300 hover:shadow-sm'
                    }
                  `}
                >
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleCluster(cluster.clusterName)}
                      className="mt-1 h-5 w-5 text-blue-600 rounded focus:ring-blue-500 cursor-pointer"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="ml-3 flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-800 mb-1">
                            {cluster.clusterName}
                          </h3>
                          {cluster.endorsement && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                              {cluster.endorsement} Endorsement
                            </span>
                          )}
                        </div>
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 ml-2">
                          {cluster.matchScore}% Match
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">
                        {cluster.reasoning}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Other / Additional Interest */}
          <div className="mt-4 border-t pt-4">
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <label htmlFor="customCareer" className="block text-sm font-semibold text-gray-700 mb-1">
                Additional Career Interest <span className="font-normal text-gray-500">(Optional)</span>
              </label>
              <p className="text-sm text-gray-500 mb-2">
                If your career interest isn't listed above, you can enter it here.
              </p>
              <input
                id="customCareer"
                type="text"
                value={customCareerChoice}
                onChange={(e) => setCustomCareerChoice(e.target.value)}
                placeholder="e.g., Marine Biology, Game Development, Environmental Law..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                maxLength={100}
              />
              <p className="text-xs text-gray-400 mt-1">
                {customCareerChoice.length}/100 characters
              </p>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div ref={errorRef} className="mb-6 p-4 bg-red-50 border-2 border-red-400 rounded-lg flex items-start space-x-3">
            <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <p className="text-red-700 font-medium">{error}</p>
          </div>
        )}

        {/* Selection Summary */}
        {(selectedClusters.length > 0 || customCareerChoice.trim()) && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-800 font-medium mb-2">
              Your Selections:
            </p>
            <div className="flex flex-wrap gap-2">
              {selectedClusters.map(cluster => (
                <span
                  key={cluster}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-600 text-white"
                >
                  {cluster}
                </span>
              ))}
              {customCareerChoice.trim() && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-600 text-white">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                  {customCareerChoice}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Submit Button */}
        <div className="flex justify-center pt-6 border-t">
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="
              px-8 py-3 rounded-lg font-semibold text-lg
              bg-blue-600 text-white hover:bg-blue-700
              disabled:bg-blue-400 disabled:cursor-not-allowed
              transition-all shadow-md hover:shadow-lg
              flex items-center space-x-2
            "
          >
            {loading ? (
              <>
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Submitting...</span>
              </>
            ) : (
              <span>Submit Survey</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClusterSelection;
